// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joyride, { EVENTS, STATUS } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import './OnboardingWizardJoyride.css';

function TourStepBody({ pourquoi, action, sousCapot }) {
  return (
    <div className="onboarding-tour-step-body">
      <p className="onboarding-tour-lead">
        <strong>À quoi ça sert :</strong> {pourquoi}
      </p>
      <p className="onboarding-tour-action">
        <strong>Action :</strong> {action}
      </p>
      <p className="onboarding-tour-tech">
        <em>Sous le capot :</em> {sousCapot}
      </p>
    </div>
  );
}

function useIsMobileNav() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

/**
 * Tutoriel produit (react-joyride). État global : `onboardingTourRun` dans AppContext.
 * Lancement exclusivement manuel (OnboardingTrigger).
 */
export default function OnboardingWizard() {
  const { onboardingTourRun, setOnboardingTourRun } = useAppContext();
  const navigate = useNavigate();
  const isMobileNav = useIsMobileNav();

  /* Ancres : `.tour-upload-btn` sur desktop (sidebar) et mobile (barre du bas) ; sélecteurs distincts car les deux nœuds existent dans le DOM. */
  const uploadSelector = isMobileNav ? '.tour-bottom-upload' : '.tour-sidebar-upload';
  const spatialSelector = isMobileNav ? '.tour-spatial-bottom' : '.tour-spatial-nav';

  const steps = useMemo(
    () => [
      {
        target: uploadSelector,
        title: '📚 Importez votre Savoir',
        content: (
          <TourStepBody
            pourquoi="C'est ici que vous indexez votre bibliothèque (PDF, EPUB, etc.) pour la recherche et l'IA."
            action="Cliquez ici ou glissez-déposez vos fichiers pour commencer."
            sousCapot="Le pipeline d'ingestion extrait le texte (et selon la config, tableaux, métadonnées) et alimente les embeddings pour la recherche sémantique."
          />
        ),
        placement: isMobileNav ? 'top' : 'right',
        disableBeacon: true,
      },
      {
        target: '.tour-chat-input',
        title: '🧠 Interrogez votre Bibliothèque',
        content: (
          <TourStepBody
            pourquoi="Ne tapez plus de simples mots-clés. Posez des questions d'analyse ou de synthèse."
            action={'Demandez par exemple : « Compare l\'idéologie politique de ces deux auteurs ».'}
            sousCapot="Le moteur RAG récupère les passages pertinents, les envoie au modèle configuré (Ollama, etc.) et peut appliquer re-classement ou garde-fous selon les réglages."
          />
        ),
        placement: 'top',
        disableBeacon: true,
      },
      {
        target: '.tour-citation-zone',
        title: '🎯 Zéro Hallucination, 100% Sourcé',
        content: (
          <TourStepBody
            pourquoi="Ne faites pas confiance à l'IA aveuglément : chaque affirmation est vérifiable."
            action="Cliquez sur n'importe quel petit numéro de (Source) dans la réponse de l'IA."
            sousCapot="Les références renvoient aux extraits indexés ; le lecteur peut surligner ou ouvrir le document pour vérifier le passage exact."
          />
        ),
        placement: 'top',
        disableBeacon: true,
      },
      {
        target: spatialSelector,
        title: "🌌 L'Espace de Réflexion Infini",
        content: (
          <TourStepBody
            pourquoi="La pensée n'est pas linéaire : organisez vos idées visuellement."
            action="Basculez sur cette vue pour transformer vos citations et vos livres en cartes déplaçables sur un tableau blanc 2D."
            sousCapot="Grâce aux technologies de synchronisation CRDT, toutes vos cartes et notes sont sauvegardées en temps réel et fusionnées sans conflit."
          />
        ),
        placement: isMobileNav ? 'top' : 'right',
        disableBeacon: true,
      },
      {
        target: '.tour-pwa-status',
        title: '⚡ Application installable (PWA)',
        content: (
          <TourStepBody
            pourquoi="Installez Biblio-Tech comme une application (Chrome / Edge : invite ou menu « Installer ») pour un raccourci bureau et une fenêtre dédiée."
            action="Sur Safari iOS : Partager → Sur l'écran d'accueil. L'indicateur en haut à droite gère aussi hors ligne et mise à jour."
            sousCapot="Le cache du navigateur et le service worker gardent l'interface utilisable ; l'IA RAG et l'indexation nécessitent le backend actif (API locale, ex. port 8898)."
          />
        ),
        placement: 'left',
        disableBeacon: true,
      },
    ],
    [uploadSelector, spatialSelector, isMobileNav]
  );

  const joyrideStyles = useMemo(
    () => ({
      options: {
        zIndex: 10050,
        primaryColor: '#4f46e5',
        textColor: '#e2e8f0',
        backgroundColor: '#0f172a',
        arrowColor: '#0f172a',
        overlayColor: 'rgba(2, 6, 23, 0.82)',
      },
      tooltip: {
        borderRadius: 14,
        padding: 18,
        maxWidth: 380,
      },
      tooltipTitle: {
        fontSize: '1.05rem',
        marginBottom: 8,
      },
      tooltipContent: {
        paddingTop: 0,
      },
      tooltipFooter: {
        marginTop: 12,
      },
      buttonNext: {
        backgroundColor: '#4f46e5',
        color: '#fff',
        borderRadius: 8,
        fontSize: '0.875rem',
      },
      buttonBack: {
        color: '#94a3b8',
        marginRight: 8,
      },
      buttonSkip: {
        color: '#94a3b8',
      },
    }),
    []
  );

  const handleCallback = useCallback(
    (data) => {
      const { status, type, index } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setOnboardingTourRun(false);
        return;
      }

      if (type === EVENTS.STEP_BEFORE && (index === 1 || index === 2)) {
        navigate('/ia');
      }
    },
    [navigate, setOnboardingTourRun]
  );

  return (
    <Joyride
      steps={steps}
      run={onboardingTourRun}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      scrollOffset={120}
      callback={handleCallback}
      styles={joyrideStyles}
      floaterProps={{
        styles: {
          floater: { filter: 'drop-shadow(0 12px 40px rgba(0,0,0,0.45))' },
        },
      }}
      locale={{
        back: '← Retour',
        close: 'Fermer',
        last: 'Terminer',
        next: 'Suivant',
        skip: 'Passer',
      }}
    />
  );
}
